package br.com.vvrefeicoes.portal.feature.auth.forgotPassword

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.ui.theme.PortalTheme
import com.composables.icons.lucide.ArrowLeft
import com.composables.icons.lucide.Check
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.EyeOff
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.X
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.auth_change_password
import portalvv.shared.generated.resources.auth_confirm_new_password
import portalvv.shared.generated.resources.auth_corporate_email
import portalvv.shared.generated.resources.auth_forgot_password_code_description
import portalvv.shared.generated.resources.auth_forgot_password_email_description
import portalvv.shared.generated.resources.auth_forgot_password_password_description
import portalvv.shared.generated.resources.auth_forgot_password_title
import portalvv.shared.generated.resources.auth_new_password
import portalvv.shared.generated.resources.auth_password_omit
import portalvv.shared.generated.resources.auth_password_show
import portalvv.shared.generated.resources.auth_send_email
import portalvv.shared.generated.resources.auth_validate_code
import portalvv.shared.generated.resources.back

@Composable
fun ForgotPasswordForm(
    controller: ForgotPasswordController,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
    ) {
        ForgotPasswordHeader(onBack = controller::back)

        when (controller.step) {
            ForgotPasswordStep.Email -> EmailStep(controller)
            ForgotPasswordStep.Code -> CodeStep(controller)
            ForgotPasswordStep.Password -> PasswordStep(controller)
        }
    }
}

@Composable
private fun ForgotPasswordHeader(onBack: () -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        AppButton(
            text = stringResource(Res.string.back),
            onClick = onBack,
            variant = PortalButtonVariant.Text,
            leadingIcon = Lucide.ArrowLeft,
        )
        Text(
            text = stringResource(Res.string.auth_forgot_password_title),
            style = MaterialTheme.typography.headlineMedium,
        )
    }
}

@Composable
private fun EmailStep(controller: ForgotPasswordController) {
    Text(text = stringResource(Res.string.auth_forgot_password_email_description))

    AppTextField(
        value = controller.email,
        onValueChange = { controller.email = it },
        label = stringResource(Res.string.auth_corporate_email),
        singleLine = true,
        enabled = !controller.loading,
        modifier = Modifier.fillMaxWidth(),
    )

    ErrorMessage(controller.error)

    AppButton(
        text = stringResource(Res.string.auth_send_email),
        onClick = controller::submitEmail,
        enabled = !controller.loading,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun CodeStep(controller: ForgotPasswordController) {
    Text(text = stringResource(Res.string.auth_forgot_password_code_description))

    OtpCodeInput(
        value = controller.code,
        onValueChange = { controller.code = it },
        length = ForgotPasswordController.CODE_LENGTH,
        modifier = Modifier.fillMaxWidth(),
    )

    ErrorMessage(controller.error)

    AppButton(
        text = stringResource(Res.string.auth_validate_code),
        onClick = controller::submitCode,
        enabled = controller.codeComplete && !controller.loading,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun PasswordStep(controller: ForgotPasswordController) {
    Text(text = stringResource(Res.string.auth_forgot_password_password_description))

    PasswordRulesChecklist(password = controller.password)

    PasswordField(
        value = controller.password,
        onValueChange = { controller.password = it },
        label = stringResource(Res.string.auth_new_password),
    )

    PasswordField(
        value = controller.confirmPassword,
        onValueChange = { controller.confirmPassword = it },
        label = stringResource(Res.string.auth_confirm_new_password),
    )

    ErrorMessage(controller.error)

    AppButton(
        text = stringResource(Res.string.auth_change_password),
        onClick = controller::submitPassword,
        enabled = !controller.loading,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun PasswordRulesChecklist(password: String) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        PasswordRule.entries.forEach { rule ->
            val satisfied = rule.isSatisfiedBy(password)
            PasswordRuleItem(
                label = rule.label,
                satisfied = satisfied,
            )
        }
    }
}

@Composable
private fun PasswordRuleItem(
    label: StringResource,
    satisfied: Boolean,
) {
    val color =
        if (satisfied) {
            PortalTheme.extraColors.success
        } else {
            MaterialTheme.colorScheme.onSurfaceVariant
        }
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Icon(
            imageVector = if (satisfied) Lucide.Check else Lucide.X,
            contentDescription = null,
            tint = color,
            modifier = Modifier.size(16.dp),
        )
        Text(
            text = stringResource(label),
            style = MaterialTheme.typography.bodyMedium,
            color = color,
        )
    }
}

@Composable
private fun PasswordField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
) {
    var visible by remember { mutableStateOf(false) }

    AppTextField(
        value = value,
        onValueChange = onValueChange,
        label = label,
        singleLine = true,
        visualTransformation =
            if (visible) VisualTransformation.None else PasswordVisualTransformation(),
        trailingIcon = {
            IconButton(onClick = { visible = !visible }) {
                Icon(
                    imageVector = if (visible) Lucide.Eye else Lucide.EyeOff,
                    contentDescription =
                        if (visible) {
                            stringResource(Res.string.auth_password_omit)
                        } else {
                            stringResource(Res.string.auth_password_show)
                        },
                )
            }
        },
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun ErrorMessage(error: ForgotPasswordError?) {
    error ?: return
    Text(
        text = stringResource(error.message),
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.error,
    )
}
